/**
 * Shared Configuration for Case Management
 *
 * Centralizes all case-related configuration used across components:
 * - CaseManagement.jsx
 * - CaseDetailPage.jsx
 * - CaseOperations.jsx
 * - CaseChecklist.jsx
 * - etc.
 */

import {
  Shield,
  User,
  FileText,
  AlertTriangle,
} from 'lucide-react';

// ===========================================
// CASE TYPE CONFIGURATION
// ===========================================
export const CASE_TYPES = {
  sanctions: {
    label: 'Sanctions',
    color: 'danger',
    icon: Shield,
    description: 'OFAC/sanctions list matches',
  },
  pep: {
    label: 'PEP',
    color: 'warning',
    icon: User,
    description: 'Politically exposed person matches',
  },
  verification: {
    label: 'Verification',
    color: 'info',
    icon: FileText,
    description: 'Document or identity verification issues',
  },
  fraud: {
    label: 'Fraud',
    color: 'danger',
    icon: AlertTriangle,
    description: 'Suspected fraudulent activity',
  },
  aml: {
    label: 'AML',
    color: 'warning',
    icon: Shield,
    description: 'Anti-money laundering alerts',
  },
};

// For dropdowns
export const CASE_TYPE_OPTIONS = Object.entries(CASE_TYPES).map(([value, config]) => ({
  value,
  label: config.label,
  description: config.description,
}));

// ===========================================
// CASE PRIORITY CONFIGURATION
// ===========================================
export const CASE_PRIORITIES = {
  critical: {
    label: 'Critical',
    color: 'danger',
    sortOrder: 0,
    slaHours: 4,
  },
  high: {
    label: 'High',
    color: 'warning',
    sortOrder: 1,
    slaHours: 24,
  },
  medium: {
    label: 'Medium',
    color: 'info',
    sortOrder: 2,
    slaHours: 72,
  },
  low: {
    label: 'Low',
    color: 'muted',
    sortOrder: 3,
    slaHours: 168, // 1 week
  },
};

// For dropdowns (ordered by severity)
export const CASE_PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

// ===========================================
// CASE STATUS CONFIGURATION
// ===========================================
export const CASE_STATUSES = {
  open: {
    label: 'Open',
    color: 'warning',
    description: 'Awaiting assignment or initial review',
  },
  in_progress: {
    label: 'In Progress',
    color: 'info',
    description: 'Being actively investigated',
  },
  pending_info: {
    label: 'Pending Info',
    color: 'muted',
    description: 'Waiting for additional information',
  },
  escalated: {
    label: 'Escalated',
    color: 'danger',
    description: 'Escalated for senior review',
  },
  resolved: {
    label: 'Resolved',
    color: 'success',
    description: 'Case closed with resolution',
  },
};

// For filters
export const CASE_STATUS_OPTIONS = Object.entries(CASE_STATUSES).map(([value, config]) => ({
  value,
  label: config.label,
}));

// Active statuses (not resolved)
export const ACTIVE_CASE_STATUSES = ['open', 'in_progress', 'pending_info', 'escalated'];

// ===========================================
// CASE RESOLUTION TYPES
// ===========================================
export const CASE_RESOLUTIONS = {
  cleared: {
    label: 'Cleared',
    color: 'success',
    description: 'No issues found, customer cleared',
  },
  rejected: {
    label: 'Rejected',
    color: 'danger',
    description: 'Customer rejected due to findings',
  },
  reported: {
    label: 'SAR Filed',
    color: 'danger',
    description: 'Suspicious Activity Report filed',
  },
  escalated: {
    label: 'Escalated',
    color: 'warning',
    description: 'Escalated to senior reviewer',
  },
  monitoring: {
    label: 'Enhanced Monitoring',
    color: 'warning',
    description: 'Cleared with ongoing monitoring',
  },
};

// For dropdowns
export const CASE_RESOLUTION_OPTIONS = Object.entries(CASE_RESOLUTIONS).map(([value, config]) => ({
  value,
  label: config.label,
  description: config.description,
}));

// ===========================================
// ESCALATION LEVELS
// ===========================================
export const ESCALATION_LEVELS = {
  1: { label: 'Level 1 - Team Lead', color: 'warning' },
  2: { label: 'Level 2 - Manager', color: 'orange' },
  3: { label: 'Level 3 - Compliance Officer', color: 'danger' },
  4: { label: 'Level 4 - Executive', color: 'danger' },
};

// ===========================================
// ASSIGNMENT MODES
// ===========================================
export const ASSIGNMENT_MODES = {
  manual: {
    label: 'Manual Assignment',
    description: 'Cases are manually assigned by a supervisor',
  },
  round_robin: {
    label: 'Round Robin',
    description: 'Cases are automatically distributed evenly',
  },
  least_loaded: {
    label: 'Least Loaded',
    description: 'Cases go to the officer with fewest open cases',
  },
};

// For dropdowns
export const ASSIGNMENT_MODE_OPTIONS = Object.entries(ASSIGNMENT_MODES).map(([value, config]) => ({
  value,
  label: config.label,
  description: config.description,
}));

// ===========================================
// VISIBLE SECTIONS (for playbooks)
// ===========================================
export const CASE_DETAIL_SECTIONS = {
  identity: { label: 'Identity Information', icon: User },
  documents: { label: 'Documents', icon: FileText },
  aml_screening: { label: 'AML Screening', icon: Shield },
  risk_overview: { label: 'Risk Overview', icon: AlertTriangle },
  verifications: { label: 'Verifications', icon: FileText },
  device_signals: { label: 'Device Signals', icon: Shield },
  related_cases: { label: 'Related Cases', icon: FileText },
};

// For multi-select
export const CASE_SECTION_OPTIONS = Object.entries(CASE_DETAIL_SECTIONS).map(([value, config]) => ({
  value,
  label: config.label,
}));

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Get badge color class for Mantine components
 */
export function getBadgeColor(color) {
  const colorMap = {
    danger: 'red',
    warning: 'yellow',
    info: 'blue',
    success: 'green',
    muted: 'gray',
    orange: 'orange',
  };
  return colorMap[color] || 'gray';
}

/**
 * Get CSS color variable for legacy components
 */
export function getCSSColor(color) {
  return `var(--${color})`;
}

/**
 * Format a datetime string to relative time
 */
export function formatRelativeTime(dateStr) {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a datetime string to full date/time
 */
export function formatDateTime(dateStr) {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get initials from a name
 */
export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

/**
 * Calculate if a case is overdue based on due_at
 */
export function isOverdue(dueAt) {
  if (!dueAt) return false;
  return new Date(dueAt) < new Date();
}

/**
 * Get hours until due (negative if overdue)
 */
export function hoursUntilDue(dueAt) {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  const now = new Date();
  return (due - now) / (1000 * 60 * 60);
}
